import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { USER_GETTING_STARTED_STEPS } from "../../../constants/dashboard";
import { updateCurrentActiveStep } from "../../../actions/dashboard";
import { useNavigate } from "react-router";
import "./styles.scss";
import { setCustomizeButtonHit } from "../../../api/dashboard";
import Slide1 from "./slide_1.jsx";
import Slide2 from "./slide_2.jsx";
import { Row, Col, Container } from "reactstrap";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import AppButton from "../../../components/common/AppButton.js";

function GettingStartedUser({ primaryColor, secondaryColor }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const stepsCompleted = useSelector(
    (state) => state?.dashboard?.customizableProductStepsForUserDone
  );

  let steps = Object.values(USER_GETTING_STARTED_STEPS);
  steps = steps.sort((a, b) => a.rankOrder - b.rankOrder);

  const onButtonClick = (key) => {
    const clickedStep = USER_GETTING_STARTED_STEPS[key];

    if (clickedStep.mappingKey === "customizeButton") {
      setCustomizeButtonHit({ customizeButton: true })
        .then(() => {})
        .catch((err) => console.log(err));
    }

    if (clickedStep?.link) {
      return window.open(clickedStep?.link, "_blank");
    }

    dispatch(updateCurrentActiveStep(key));
    navigate(clickedStep?.redirectRoute);
  };
  const [hoveredSide, setHoveredSide] = useState(null);

  const [isChecklistVisible, setChecklistVisible] = useState(true); // Checklist visibility toggle
  const toggleChecklist = () => {
    setChecklistVisible(!isChecklistVisible);
  };
  useEffect(() => {
    console.log("Theme primary color:", theme);
    const drawer = document.querySelector(".MuiPaper-root");

    if (drawer) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect.width < 300) {
            drawer.classList.add("small-drawer");
          } else {
            drawer.classList.remove("small-drawer");
          }
        }
      });

      resizeObserver.observe(drawer);

      return () => {
        resizeObserver.disconnect();
      };
    }
  }, []);

  const getColStyle = (stepKey) => {
    const isStepCompleted = stepsCompleted.includes(stepKey);

    // If this column is disabled, return greyed out style
    if (isStepCompleted) {
      return {
        flex: "0 0 38%", // Take remaining space when other is disabled
        transition: "flex 0.3s ease-in-out",
        cursor: "not-allowed",
        opacity: 0.5,
        filter: "grayscale(100%)",
      };
    }

    // If the other step is completed, this one should be larger
    const otherStep = steps.find((step) => step.key !== stepKey);
    const isOtherStepCompleted =
      otherStep && stepsCompleted.includes(otherStep.key);

    if (isOtherStepCompleted) {
      return {
        flex: "0 0 58%", // Take remaining space when other is disabled
        transition: "flex 0.3s ease-in-out",
        cursor: "pointer",
      };
    }

    // Normal hover behavior when both columns are active
    let flex = "0 0 48%"; // Default ~50% with some margin

    if (hoveredSide === stepKey) {
      flex = "0 0 58%"; // Expanded but stays under 60%
    } else if (hoveredSide && hoveredSide !== stepKey) {
      flex = "0 0 38%"; // Shrunk
    }

    return {
      flex: flex,
      transition: "flex 0.3s ease-in-out",
      cursor: "pointer",
    };
  };

  return (
    <Col>
      <Container
        fluid
        className="p-0"
        style={{
          width: "100%",
        }}
      >
        <Row
          className="h-100
              flex-column flex-lg-row flex-lg-nowrap
              justify-content-center"
          style={{
            gap: "5px",
          }}
        >
          {steps.map((step, index) => {
            const isCompleted = stepsCompleted.includes(step.key);
            const isFirstStep = index === 0;

            return (
              <Col
                key={step.key}
                className="flex-column align-items-start justify-content-end"
                style={{
                  ...getColStyle(step.key),
                  backgroundColor:
                    hoveredSide === step.key ? "#505050" : "#ffffff",
                  color: hoveredSide === step.key ? "#505050" : "white",
                  padding: "2rem",
                  borderRadius: "20px 20px 20px 20px",
                  minHeight: "100%",
                }}
                onMouseEnter={() => !isCompleted && setHoveredSide(step.key)}
                onMouseLeave={() => !isCompleted && setHoveredSide(null)}
                onClick={() => !isCompleted && onButtonClick(step.key)}
              >
                {isFirstStep ? (
                  <Slide1
                    // width 150px till md then 200px for lg and above
                    //if hovering over the second step, use primaryColor and secondaryColor
                    fillColor={hoveredSide === step.key ? "#ffffff" : "#000000"}
                    iconFill={primaryColor}
                    style={{
                      width: isMdDown ? "150px" : "200px",
                      height: "auto",
                      stroke: "white",
                      borderRadius: "20px 20px 0 0",
                    }}
                  />
                ) : (
                  <Slide2
                    fillColor={hoveredSide === step.key ? "#ffffff" : "#000000"}
                    iconFill={primaryColor}
                    style={{
                      width: isMdDown ? "150px" : "200px",
                      height: "auto",
                      stroke: "white",
                      borderRadius: "20px 20px 0 0",
                    }}
                  />
                )}

                <div
                  style={{
                    fontSize: "3rem",
                    marginBottom: "1rem",
                  }}
                >
                  {/* Icon placeholder */}
                </div>

                <p
                  className="text-muted mb-1"
                  style={{
                    fontSize: "0.875rem",
                    opacity: 0.8,
                  }}
                >
                  Step {index + 1}
                </p>

                <h5
                  className={`mb-2 fw-bold ${
                    hoveredSide === step.key ? "text-white" : "text-[#505050]"
                  }`}
                  style={{
                    fontWeight: "600",
                    //this should take 2 lines even if the text fits in one line
                  }}
                >
                  {step.title}
                </h5>

                <h5
                  style={{
                    fontSize: "0.9rem",
                    opacity: 0.9,
                    fontWeight: 600,
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    minHeight: "3em", // Adjust based on your line-height
                    lineHeight: "1.5em", // Ensures consistent spacing
                  }}
                  className={
                    hoveredSide === step.key ? "text-white" : "text-[#505050]"
                  }
                >
                  {step.content}
                </h5>

                <AppButton
                  // color={hoveredSide === step.key ? "light" : "primary"}
                  // className={`mt-3 fw-bold ${
                  //   hoveredSide === step.key ? "text-dark" : ""
                  // }`}
                  // style={{
                  //   borderRadius: "20px",
                  // }}
                  disabled={isCompleted}
                >
                  <div
                    style={{
                      color: hoveredSide === step.key ? undefined : "white",
                    }}
                  >
                    {step.buttonText}
                  </div>
                </AppButton>
              </Col>
            );
          })}
        </Row>
      </Container>
    </Col>
  );
}
export default GettingStartedUser;
